import React from 'react';
import { BadgeCheck, Info, Trophy } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { BADGE_COPY } from '@/data/legal';

interface Props {
  licenseVerified?: boolean | null;
  insuranceVerified?: boolean | null;
  backgroundVerified?: boolean | null;
  /** Trigger-maintained mkt_profiles.jobs_won. >= 1 shows the "GigGone Hired" trust badge. */
  jobsWon?: number | null;
  /** Show an explicit "not verified" note when nothing has been verified. */
  showUnverifiedNote?: boolean;
  className?: string;
}

/**
 * Renders trust badges ONLY when the platform has actually completed the
 * verification. Nothing here is inferred from user-submitted data — a license
 * number on file does NOT produce a badge. "GigGone Hired" comes from a
 * homeowner-recorded hire (server-maintained counter), never from the contractor.
 */
const VerificationBadges: React.FC<Props> = ({
  licenseVerified,
  insuranceVerified,
  backgroundVerified,
  jobsWon,
  showUnverifiedNote = false,
  className = '',
}) => {
  const { lang } = useI18n();
  const pick = (v: { en: string; es: string }) => (lang === 'es' ? v.es : v.en);
  const hired = Number(jobsWon || 0) >= 1;

  const badges = [
    licenseVerified ? pick(BADGE_COPY.license) : null,
    insuranceVerified ? pick(BADGE_COPY.insurance) : null,
    backgroundVerified ? pick(BADGE_COPY.background) : null,
  ].filter(Boolean) as string[];

  const hiredBadge = hired ? (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-200"
      title={lang === 'es' ? `Contratado ${jobsWon} ${Number(jobsWon) === 1 ? 'vez' : 'veces'} a través de GigGone` : `Hired ${jobsWon} ${Number(jobsWon) === 1 ? 'time' : 'times'} through GigGone`}
    >
      <Trophy className="h-3.5 w-3.5" /> {lang === 'es' ? 'Contratado en GigGone' : 'GigGone Hired'}
      {Number(jobsWon) > 1 ? ` ×${jobsWon}` : ''}
    </span>
  ) : null;

  if (badges.length === 0 && hiredBadge) return <div className={`flex flex-wrap gap-2 ${className}`}>{hiredBadge}</div>;

  if (badges.length === 0) {
    if (!showUnverifiedNote) return null;
    return (
      <p className={`flex items-start gap-1.5 text-xs text-slate-500 ${className}`}>
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {pick(BADGE_COPY.none)}
      </p>
    );
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {hiredBadge}
      {badges.map((b) => (
        <span
          key={b}
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200"
        >
          <BadgeCheck className="h-3.5 w-3.5" /> {b}
        </span>
      ))}
    </div>
  );
};

export default VerificationBadges;
